import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AmplifyApiService } from '@nx/amplify-api';
import { environment } from 'apps/learn-my-way/src/environments/environment';
import { Observable } from 'rxjs';
import { map, pluck, publishReplay, refCount, take } from 'rxjs/operators';
import { TopicAttemptStore } from '../interfaces/topic-attempt-store.interface';
import { Topic } from '../interfaces/topic.interface';
import { Subject } from '../interfaces/subject.interface';
import { TopicAttempt } from '../interfaces/topic-attempt.interface';

@Injectable({
  providedIn: 'root'
})
export class TopicService {

  private cache: { [name: string]: Observable<any> } = {};

  constructor(private http:HttpClient, private amplifyApiService: AmplifyApiService) { }

  public getTopicBySlugAndLocale(topicSlug:string, locale:string): Observable<Topic> {
    const url = `/config/${locale}/topics/${topicSlug}.json`;
    if (!this.cache[url]) {
      this.cache[url] = this.http.get<Topic>(url).pipe(publishReplay(), refCount());
    }
    return this.cache[url];
  }

  public getTopicsBySubjectAndLocale(subjectSlug: string, locale: string): Observable<Topic[]> {
    return this.getSubjectByLocale(subjectSlug, locale).pipe(pluck('topics'));
  }

  public getSubjectImageNumber(subjectSlug: string, locale: string): Observable<string> {
    return this.getSubjectByLocale(subjectSlug, locale).pipe(pluck('subjectImage'));
  }

  public getSubjectByLocale(subjectSlug: string, locale: string): Observable<Subject> {
    const url = `/config/${locale}/subjects/${subjectSlug}.json`;
    if (!this.cache[url]) {
      this.cache[url] = this.http.get<Subject>(url).pipe(publishReplay(), refCount());
    }
    return this.cache[url];
  }

  public getTopicsAttemptsByCompleted(completed:boolean): Observable<TopicAttempt[]> {
    return this.amplifyApiService.privateGet('/core-new/topic-attempt?completed=' + completed);
  }

  public getSubjectsByLocale(locale: string) {
    return this.http.get(`/config/${locale}/subjects.json`);
  }

  public doesTopicBelongToSubject(subjectSlug: string, topicSlug: string, locale: string): Observable<boolean> {
    return this.getTopicsBySubjectAndLocale(subjectSlug, locale).pipe(
      take(1),
      map((topics:any[]) => topics.some(topic => topic.sk === topicSlug))
    )
  }

  public createTopicAttempt(topicSlug:string) :Observable<any> {
    return this.amplifyApiService.privatePost('/core-new/topic-attempt', { topicSlug: topicSlug });
  }

  public completeTopicAttempt(topicAttemptSortKey: string, subjectSlug: string): Observable<TopicAttemptStore> {
    return this.amplifyApiService.privatePatch('/core-new/topic-attempt/complete',
      {
        SK: topicAttemptSortKey,
        subjectSk: subjectSlug,
        locale: environment.localeId
      }
    );
  }
}
